import { isAndroid, isIOS } from 'react-device-detect';
import type { paths } from '../../types/authSchema';
import { authApiClient, resolveAuthApiErrorMessage } from './authApiClient';

export type SocialProvider = 'google' | 'kakao' | 'apple' | 'facebook';
export type SocialPlatform = 'web' | 'ios' | 'android';

export interface SocialLoginMethod {
  provider: SocialProvider;
  display_name: string;
  priority?: number;
  auth_enabled?: boolean;
  rollout_stage?: string;
  supported_platforms?: SocialPlatform[];
  login_hint?: string;
}

export interface SocialLoginMethodsResponse {
  success?: boolean;
  country?: string;
  countrySource?: string;
  platform?: SocialPlatform | string;
  loginMethods?: SocialLoginMethod[];
  enabledLoginMethods?: SocialLoginMethod[];
  errorMessage?: string;
}

interface GetSocialLoginMethodsParams {
  platform: SocialPlatform;
  country?: string;
}

type SocialLoginMethodsApiResponse =
  paths['/api/v1/auth/social_login_methods']['get']['responses']['200']['content']['application/json'];

type SocialLoginMethodsQuery = NonNullable<
  paths['/api/v1/auth/social_login_methods']['get']['parameters']['query']
>;

const SOCIAL_PROVIDERS: SocialProvider[] = ['google', 'kakao', 'apple', 'facebook'];
const SOCIAL_PLATFORMS: SocialPlatform[] = ['web', 'ios', 'android'];

export function getClientSocialPlatform(): SocialPlatform {
  if (isAndroid) return 'android';
  if (isIOS) return 'ios';
  return 'web';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

function isSocialProvider(value: string): value is SocialProvider {
  return SOCIAL_PROVIDERS.includes(value as SocialProvider);
}

function isSocialPlatform(value: string): value is SocialPlatform {
  return SOCIAL_PLATFORMS.includes(value as SocialPlatform);
}

function parseSocialLoginMethod(raw: unknown): SocialLoginMethod | null {
  const record = asRecord(raw);
  if (!record) return null;

  const providerRaw = typeof record.provider === 'string' ? record.provider : null;
  if (!providerRaw || !isSocialProvider(providerRaw)) return null;

  const displayName =
    typeof record.display_name === 'string'
      ? record.display_name
      : typeof record.displayName === 'string'
        ? record.displayName
        : providerRaw;

  const supportedPlatforms = Array.isArray(record.supported_platforms)
    ? record.supported_platforms
        .filter((platform): platform is string => typeof platform === 'string')
        .filter(isSocialPlatform)
    : undefined;

  return {
    provider: providerRaw,
    display_name: displayName,
    priority: typeof record.priority === 'number' ? record.priority : undefined,
    auth_enabled: typeof record.auth_enabled === 'boolean' ? record.auth_enabled : undefined,
    rollout_stage: typeof record.rollout_stage === 'string' ? record.rollout_stage : undefined,
    supported_platforms: supportedPlatforms,
    login_hint: typeof record.login_hint === 'string' ? record.login_hint : undefined
  };
}

function normalizeSocialLoginMethods(rawMethods: unknown): SocialLoginMethod[] {
  if (!Array.isArray(rawMethods)) return [];

  return rawMethods
    .map(parseSocialLoginMethod)
    .filter((method): method is SocialLoginMethod => method !== null);
}

function normalizeSocialLoginMethodsResponse(
  response: SocialLoginMethodsApiResponse
): SocialLoginMethodsResponse {
  return {
    success: response.success,
    country: response.country,
    countrySource: response.countrySource,
    platform: response.platform,
    loginMethods: normalizeSocialLoginMethods(response.loginMethods),
    enabledLoginMethods: normalizeSocialLoginMethods(response.enabledLoginMethods)
  };
}

export async function getSocialLoginMethods({
  platform,
  country
}: GetSocialLoginMethodsParams): Promise<SocialLoginMethodsResponse> {
  const query: SocialLoginMethodsQuery = { platform };
  if (country) {
    query.country = country;
  }

  try {
    const { data, error } = await authApiClient.GET('/api/v1/auth/social_login_methods', {
      params: {
        query
      }
    });

    if (data) {
      return normalizeSocialLoginMethodsResponse(data);
    }

    return {
      success: false,
      platform,
      loginMethods: [],
      enabledLoginMethods: [],
      errorMessage: resolveAuthApiErrorMessage(
        error,
        'Failed to load social login methods. Please try again.'
      )
    };
  } catch (error) {
    return {
      success: false,
      platform,
      loginMethods: [],
      enabledLoginMethods: [],
      errorMessage: resolveAuthApiErrorMessage(
        error,
        'Failed to load social login methods. Please try again.'
      )
    };
  }
}

function isPlatformSupported(method: SocialLoginMethod, platform: SocialPlatform) {
  if (!method.supported_platforms || method.supported_platforms.length === 0) {
    return true;
  }
  return method.supported_platforms.includes(platform);
}

function sortByPriorityAsc(a: SocialLoginMethod, b: SocialLoginMethod) {
  const aPriority = a.priority ?? Number.MAX_SAFE_INTEGER;
  const bPriority = b.priority ?? Number.MAX_SAFE_INTEGER;
  return aPriority - bPriority;
}

export function resolveEnabledSocialLoginMethods(
  response: SocialLoginMethodsResponse,
  platform: SocialPlatform
) {
  const methods =
    response.enabledLoginMethods && response.enabledLoginMethods.length > 0
      ? response.enabledLoginMethods
      : response.loginMethods || [];

  return methods
    .filter((method) => method.auth_enabled !== false)
    .filter((method) => isPlatformSupported(method, platform))
    .sort(sortByPriorityAsc);
}
